import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";

import InputWrapper from "../utilities/formElements/commonInput/InputWrapper";
import CommonButton from "../utilities/formElements/commonButton/CommonButton";
import { changePasswordValidationSchema } from "../utilities/validationSchema";
import { toastMessageError } from "../utilities/commonToastMessage";

import { changePassword } from "../../services/user";

import {
  CommonModalProps,
  IPassword,
} from "../../Interfaces/CommonModalInterface";
import { typePassword, typeText } from "../../constants/commonConstants";

import EyeIcon from "../../assets/images/eye-outline.svg";
import EyeCloseIcon from "../../assets/images/hidden-password.svg";
import CheckMark from "../../assets/images/check-mark.svg";

const ChangePassword: React.FC<CommonModalProps> = ({
  title,
  onClose,
  closeModel,
}) => {
  const { t: translation } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [viewPasswordAndConfirmPassword, setViewPasswordAndConfirmPasword] =
    useState({ viewPassword: false, viewConfirmPassword: false });
  // Was: success toast + immediate forced logout with zero explanation -
  // the redirect fired before the toast was even readable, and there was
  // no messaging anywhere that changing your password would log you out.
  // Explicit decision: stay logged in, show a real confirmation instead.
  const [successModal, setSuccessModal] = useState(false);

  const {
    handleSubmit: handleSubmitForChangePassword,
    formState: { errors: errorsForChangePassword },
    control: controlForChangePasswordForm,
  } = useForm({
    resolver: yupResolver(changePasswordValidationSchema(translation)),
  });

  const formDataSubmitForChangePassword = async (data: IPassword) => {
    setLoading(true);

    const result = await changePassword(data);
    if (result && result.data) {
      setSuccessModal(true);
    } else {
      if (
        result &&
        result.error &&
        result.error.message === "Your old password not matched"
      ) {
        toastMessageError(result?.error?.message, "oldPasswordIncorrect");
      }
    }
    setLoading(false);
  };

  if (successModal) {
    return (
      <div className="modal show-modal common-modal">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-body common-body text-primary tracking-code-modal">
              <img
                src={CheckMark}
                alt="success"
                className="successful-icon"
              />
              <div className="congrats-header">
                <h4>{translation("pass_changes_successfully")}</h4>
              </div>
              <div className="button-align-modal flex-align-modal-btn">
                <CommonButton
                  className="theme-btn success-btn text-uppercase fontBold"
                  type="button"
                  label={translation("ok")}
                  onClick={onClose}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal show-modal common-modal">
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h4 className="modal-title">{title}</h4>
            <CommonButton
              type="button"
              className="btnC btn-close btn-close-white"
              onClick={onClose}
              data-bs-dismiss="modal"
            />{" "}
          </div>

          <div className="modal-body common-body text-primary">
            <form
              onSubmit={handleSubmitForChangePassword((data) =>
                formDataSubmitForChangePassword(data as IPassword)
              )}
            >
              <div className="form-group">
                <InputWrapper
                  label={translation("old_password")}
                  control={controlForChangePasswordForm}
                  name="oldPassword"
                  className="form-control"
                  placeholder={translation("old_password")}
                  error={errorsForChangePassword?.oldPassword}
                  type="password"
                />
              </div>

              <div className="form-group">
                <label>{translation("new_password")}</label>
                <div className="input-icon">
                  <InputWrapper
                    control={controlForChangePasswordForm}
                    name="newPassword"
                    className="form-control"
                    placeholder={translation("new_password")}
                    error={errorsForChangePassword?.newPassword}
                    type={
                      viewPasswordAndConfirmPassword.viewPassword
                        ? typeText
                        : typePassword
                    }
                  />
                  <CommonButton
                    type="button"
                    onClick={() =>
                      setViewPasswordAndConfirmPasword((prevState) => ({
                        ...prevState,
                        viewPassword:
                          !viewPasswordAndConfirmPassword.viewPassword,
                      }))
                    }
                    iconSrc={
                      viewPasswordAndConfirmPassword.viewPassword
                        ? EyeIcon
                        : EyeCloseIcon
                    }
                  />
                </div>
              </div>

              <div className="form-group">
                <label>{translation("confirm_password")}</label>
                <div className="input-icon">
                  <InputWrapper
                    control={controlForChangePasswordForm}
                    name="confirmPassword"
                    className="form-control"
                    placeholder={translation("confirm_new_password")}
                    error={errorsForChangePassword?.confirmPassword}
                    type={
                      viewPasswordAndConfirmPassword.viewConfirmPassword
                        ? typeText
                        : typePassword
                    }
                  />

                  <CommonButton
                    type="button"
                    onClick={() =>
                      setViewPasswordAndConfirmPasword((prevState) => ({
                        ...prevState,
                        viewConfirmPassword:
                          !viewPasswordAndConfirmPassword.viewConfirmPassword,
                      }))
                    }
                    iconSrc={
                      viewPasswordAndConfirmPassword.viewConfirmPassword
                        ? EyeIcon
                        : EyeCloseIcon
                    }
                  />
                </div>
              </div>

              <div className="text-center mt-4 mb-3">
                <CommonButton
                  type="submit"
                  className="theme-btn success-btn text-uppercase fontBold"
                  label={translation("change_password")}
                  loading={loading}
                  disabled={loading}
                />
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};
export default ChangePassword;
